import moment from 'moment-timezone';

import {
  getExtendedCampaignEnd,
  isWithinPostCampaignWindow
} from '@/components/driveToStore/common/helpers/campaignDates.helper';

import type { TrafficAnalysis as TrafficAnalysisData } from 'sonata-static-storage/lib/entities/footfallReport/footfallReport.types';

export type Granularity = 'all' | 'month' | 'week' | 'day';

export type PeriodInfo = {
  currentPeriodIndex: number;
  periodLabels: string[];
  totalPeriods: number;
};

export type PeriodRange = { end: number; start: number };

type PeriodUnit = 'day' | 'isoWeek' | 'month';
type PeriodDiffUnit = 'days' | 'months' | 'weeks';

type GranularityConfig = {
  diffUnit: PeriodDiffUnit;
  formatLabel: (periodStart: moment.Moment) => string;
  unit: PeriodUnit;
};

const GRANULARITY_CONFIG: Record<Exclude<Granularity, 'all'>, GranularityConfig> = {
  day: {
    diffUnit: 'days',
    formatLabel: periodStart => periodStart.format('dddd, MMMM D'),
    unit: 'day'
  },
  month: {
    diffUnit: 'months',
    formatLabel: periodStart => periodStart.format('MMMM YYYY'),
    unit: 'month'
  },
  week: {
    diffUnit: 'weeks',
    formatLabel: periodStart =>
      `${periodStart.format('MMMM D')} – ${periodStart.clone().endOf('isoWeek').format('MMMM D')}`,
    unit: 'isoWeek'
  }
};

function getFirstPeriodStart(
  granularity: Exclude<Granularity, 'all'>,
  campaignStart: string,
  timezone: string
): moment.Moment {
  return moment.tz(campaignStart, timezone).startOf(GRANULARITY_CONFIG[granularity].unit);
}

function getEffectiveEndMs(campaignEnd: string, timezone: string, dataEndBoundMs?: number): number {
  const campaignEndMs = moment.tz(campaignEnd, timezone).valueOf();
  return dataEndBoundMs !== undefined ? Math.max(campaignEndMs, dataEndBoundMs) : campaignEndMs;
}

function getEffectiveEnd(
  granularity: Exclude<Granularity, 'all'>,
  campaignEnd: string,
  timezone: string,
  dataEndBoundMs?: number
): moment.Moment {
  const effectiveEndMs = getEffectiveEndMs(campaignEnd, timezone, dataEndBoundMs);
  return moment.tz(effectiveEndMs, timezone).startOf(GRANULARITY_CONFIG[granularity].unit);
}

function getCurrentPeriodReferenceDate(
  granularity: Granularity,
  campaignEnd: string,
  timezone: string,
  unit: PeriodUnit
): moment.Moment {
  if (granularity !== 'day') {
    return moment.tz(timezone).startOf(unit);
  }

  if (isWithinPostCampaignWindow(campaignEnd, timezone)) {
    return moment.tz(timezone).startOf('day').subtract(1, 'day');
  }

  return moment.tz(getExtendedCampaignEnd(campaignEnd, timezone), timezone).startOf('day');
}

function formatCampaignRangeLabel(campaignStart: string, effectiveEndMs: number, timezone: string): string {
  const start = moment.tz(campaignStart, timezone);
  const end = moment.tz(effectiveEndMs, timezone);
  const startFormat = start.year() === end.year() ? 'MMMM D' : 'MMMM D, YYYY';
  return `${start.format(startFormat)} - ${end.format('MMMM D, YYYY')}`;
}

export function getPeriodInfo(
  granularity: Granularity,
  campaignStart: string,
  campaignEnd: string,
  timezone: string,
  dataEndBoundMs?: number
): PeriodInfo {
  if (granularity === 'all') {
    const effectiveEndMs = getEffectiveEndMs(campaignEnd, timezone, dataEndBoundMs);
    return {
      currentPeriodIndex: 0,
      periodLabels: [formatCampaignRangeLabel(campaignStart, effectiveEndMs, timezone)],
      totalPeriods: 1
    };
  }

  const { diffUnit, formatLabel, unit } = GRANULARITY_CONFIG[granularity];
  const firstPeriodStart = getFirstPeriodStart(granularity, campaignStart, timezone);
  const effectiveEnd = getEffectiveEnd(granularity, campaignEnd, timezone, dataEndBoundMs);
  const totalPeriods = Math.max(1, effectiveEnd.diff(firstPeriodStart, diffUnit) + 1);

  const referenceDate = getCurrentPeriodReferenceDate(granularity, campaignEnd, timezone, unit);
  const currentPeriodIndex = Math.max(0, Math.min(referenceDate.diff(firstPeriodStart, diffUnit), totalPeriods - 1));

  const periodLabels = Array.from({ length: totalPeriods }, (_, i) =>
    formatLabel(firstPeriodStart.clone().add(i, diffUnit))
  );

  return { currentPeriodIndex, periodLabels, totalPeriods };
}

export function getPeriodRange(
  granularity: Granularity,
  periodIndex: number,
  campaignStart: string,
  timezone: string
): PeriodRange | null {
  if (granularity === 'all') {
    return null;
  }

  const { diffUnit, unit } = GRANULARITY_CONFIG[granularity];
  const firstPeriodStart = getFirstPeriodStart(granularity, campaignStart, timezone);
  const periodStart = firstPeriodStart.clone().add(periodIndex, diffUnit);
  const periodEnd = periodStart.clone().endOf(unit);

  return { end: periodEnd.valueOf(), start: periodStart.valueOf() };
}

export function filterTrafficDataByRange(data: TrafficAnalysisData, range: PeriodRange | null): TrafficAnalysisData {
  if (range === null) {
    return data;
  }

  const inRange = (point: { date: string }): boolean => {
    const timestamp = new Date(point.date).getTime();
    return timestamp >= range.start && timestamp <= range.end;
  };

  return {
    baseline: data.baseline.filter(inRange),
    baselinePrediction: data.baselinePrediction.filter(inRange),
    uplift: data.uplift.filter(inRange),
    upliftPrediction: data.upliftPrediction.filter(inRange)
  };
}
